import {
    Twitter, Linkedin, Github, Instagram, Globe, Facebook, Youtube, Mail, Link as LinkIcon,
} from 'lucide-vue-next';

export const socialIcon = (platform: string): any => {
    const map: Record<string, any> = {
        twitter: Twitter,
        x: Twitter,
        linkedin: Linkedin,
        github: Github,
        instagram: Instagram,
        facebook: Facebook,
        youtube: Youtube,
        email: Mail,
        mail: Mail,
        website: Globe,
        web: Globe,
        site: Globe,
    };
    return map[(platform || '').toLowerCase()] || LinkIcon;
};

export const socialLabel = (platform: string) =>
    (platform || 'link').charAt(0).toUpperCase() + (platform || 'link').slice(1).toLowerCase();

// Common country name -> ISO 3166-1 alpha-2 (extend as needed).
const countryCodes: Record<string, string> = {
    indonesia: 'ID', oman: 'OM', 'united states': 'US', usa: 'US', 'united kingdom': 'GB',
    uk: 'GB', tanzania: 'TZ', kenya: 'KE', uganda: 'UG', rwanda: 'RW', nigeria: 'NG',
    ghana: 'GH', 'south africa': 'ZA', egypt: 'EG', india: 'IN', china: 'CN', japan: 'JP',
    germany: 'DE', france: 'FR', spain: 'ES', italy: 'IT', netherlands: 'NL', canada: 'CA',
    australia: 'AU', brazil: 'BR', 'saudi arabia': 'SA', 'united arab emirates': 'AE',
    uae: 'AE', qatar: 'QA', kuwait: 'KW', bahrain: 'BH', pakistan: 'PK', bangladesh: 'BD',
    malaysia: 'MY', singapore: 'SG', philippines: 'PH', thailand: 'TH', vietnam: 'VN',
    'south korea': 'KR', turkey: 'TR', 'new zealand': 'NZ', ireland: 'IE', portugal: 'PT',
    belgium: 'BE', switzerland: 'CH', sweden: 'SE', norway: 'NO', denmark: 'DK', finland: 'FI',
    ethiopia: 'ET', morocco: 'MA', zambia: 'ZM', zimbabwe: 'ZW', botswana: 'BW', malawi: 'MW',
};

// Resolve a free-text country (name or ISO2 code) to a flag emoji, or '' if unknown.
export const countryFlag = (country?: string): string => {
    if (!country) return '';
    const raw = country.trim();
    let code = /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : countryCodes[raw.toLowerCase()];
    if (!code) return '';
    return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

export const formatMoney = (amount: number, currency = 'USD') => {
    if (!amount || amount <= 0) return 'Free';
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch (e) {
        return `${currency} ${amount}`;
    }
};

export const initials = (name: string) =>
    (name || '')
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();


export const parseFullName = (fullName: string): {
    firstName: string;
    secondName: string | null;
    lastName: string;
} => {
    // Trim and split by spaces
    const parts = fullName.trim().split(/\s+/);

    // Common titles to filter out
    const titles = /^(mr|mrs|ms|dr|prof|eng|sir|madam|miss|lord|lady|rev|hon)\.?$/i;

    // Filter out titles
    const nameParts = parts.filter(part => !titles.test(part));

    // If we filtered out everything, fallback to original parts minus first item
    if (nameParts.length === 0 && parts.length > 0) {
        nameParts.push(...parts.slice(1));
    }

    let firstName = '';
    let secondName: string | null = null;
    let lastName = '';

    if (nameParts.length === 1) {
        // Only one name
        firstName = nameParts[0];
    } else if (nameParts.length === 2) {
        // First and last name
        firstName = nameParts[0];
        lastName = nameParts[1];
    } else if (nameParts.length >= 3) {
        // First, second(s), and last name
        firstName = nameParts[0];
        lastName = nameParts[nameParts.length - 1];
        // Everything in between is the second name
        secondName = nameParts.slice(1, -1).join(' ');
    }

    return {
        firstName: firstName || '',
        secondName: secondName || null,
        lastName: lastName || ''
    };
}
